import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { TREATMENT_PAGES } from "../data/treatment-pages";
import { useBookingLink } from "../lib/useBookingLinks";
import { CHERRY_URL, PHONE_DISPLAY, PHONE_TEL } from "../data/site";
import { trackSchedule } from "../lib/tracking";

// FAQ structured data so Google can show the questions in search results.
const useFaqSchema = (page) => {
  useEffect(() => {
    if (!page) return undefined;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: page.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [page]);
};

// Peel pages link to the VI Peel vs Cosmelan quiz.
const PEEL_SLUGS = ["vi-peel-scottsdale", "cosmelan-peel-scottsdale"];

// Booking button; contact-only treatments send people to the Contact page instead of Vagaro.
const Cta = ({ p, book, className, testid, children }) => (p.contactOnly
  ? <Link to="/contact-us" className={className} data-testid={testid}>Contact Us to Schedule</Link>
  : <a href={book} target="_blank" rel="noreferrer" onClick={trackSchedule} className={className} data-testid={testid}>{children}</a>);

const Hero = ({ p, book }) => (
  <section className="grid lg:grid-cols-2">
    <div className="flex flex-col justify-center px-6 py-16 md:px-16 lg:py-28">
      <p className="kicker rise">{p.kicker}</p>
      <h1 className="font-display rise d1 mt-4 text-4xl font-light leading-[1.05] text-neutral-950 sm:text-5xl lg:text-6xl" data-testid="tp-title">{p.name}</h1>
      <p className="font-display rise d1 mt-3 text-2xl italic text-neutral-600">{p.headline}</p>
      <p className="rise d2 mt-6 text-xs uppercase tracking-[0.25em] text-neutral-950" data-testid="tp-price">{p.price}</p>
      <p className="rise d2 mt-6 max-w-lg text-base font-light leading-relaxed text-neutral-600">{p.intro}</p>
      <div className="rise d3 mt-10 flex flex-col gap-4 sm:flex-row">
        <Cta p={p} book={book} className="btn-ink" testid="tp-book-btn">Book {p.name}</Cta>
        {p.contactOnly
          ? <a href={`tel:${PHONE_TEL}`} className="btn-line" data-testid="tp-call-btn">Call {PHONE_DISPLAY}</a>
          : <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="btn-line" data-testid="tp-cherry-btn">Finance with Cherry</a>}
      </div>
      {PEEL_SLUGS.includes(p.slug) && <Link to="/which-peel-is-right-for-me" className="link-under rise d3 mt-8 self-start" data-testid="tp-peel-quiz-link">Not sure? Take the peel quiz</Link>}
    </div>
    <div className="h-[380px] overflow-hidden lg:h-[720px]"><img src={p.image} alt={`${p.name} at Lux Veritae Skin Spa, Scottsdale`} className="h-full w-full object-cover" /></div>
  </section>
);

const Treats = ({ p }) => (
  <section className="bg-[hsl(var(--stone))] py-20 md:py-28" data-testid="tp-treats">
    <div className="mx-auto max-w-[1200px] px-6 md:px-8">
      <Reveal><h2 className="font-display text-4xl font-light text-neutral-950 md:text-5xl">What it treats</h2></Reveal>
      <ul className="mt-10 grid gap-px bg-neutral-300 sm:grid-cols-2 lg:grid-cols-3">
        {p.treats.map((t) => <li key={t} className="bg-[hsl(var(--stone))] p-6 font-display text-xl text-neutral-900">{t}</li>)}
      </ul>
    </div>
  </section>
);

const Steps = ({ p }) => (
  <section className="bg-white py-20 md:py-28" data-testid="tp-steps">
    <div className="mx-auto grid max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-[1fr_1.4fr]">
      <Reveal>
        <h2 className="font-display text-4xl font-light text-neutral-950 md:text-5xl">What to expect</h2>
        <p className="mt-6 text-base font-light leading-relaxed text-neutral-600">{p.results}</p>
      </Reveal>
      <ol className="space-y-8">
        {p.steps.map(([t, d], i) => (
          <li key={t} className="flex gap-6 border-t border-neutral-200 pt-6"><span className="font-display text-3xl text-neutral-400">0{i + 1}</span><div><p className="font-display text-2xl text-neutral-950">{t}</p><p className="mt-1 text-sm font-light leading-relaxed text-neutral-600">{d}</p></div></li>
        ))}
      </ol>
    </div>
  </section>
);

const Pricing = ({ p, book }) => (
  <section className="bg-neutral-950 py-20 text-white md:py-28" data-testid="tp-pricing">
    <div className="mx-auto max-w-3xl px-6">
      <p className="text-[11px] uppercase tracking-[0.35em] text-neutral-400">Pricing</p>
      <h2 className="font-display mt-3 text-4xl font-light md:text-5xl">{p.name}</h2>
      <ul className="mt-10 border-t border-neutral-800">
        {p.pricing.map(([k, v], i) => (
          <li key={k} className="flex items-baseline justify-between gap-6 border-b border-neutral-800 py-5" data-testid={`tp-price-row-${i}`}>
            <span className="font-display text-xl text-neutral-100">{k}</span>
            <span className="text-sm uppercase tracking-[0.2em] text-white">{v}</span>
          </li>
        ))}
      </ul>
      <p className="mt-6 text-xs font-light text-neutral-400">Final pricing is confirmed at your consultation. Cherry financing available.</p>
      <Cta p={p} book={book} className="btn-ghost-light mt-10" testid="tp-pricing-book-btn">Book {p.name}</Cta>
    </div>
  </section>
);

const Faqs = ({ p, book }) => (
  <section className="bg-[hsl(var(--stone))] py-20 md:py-28" data-testid="tp-faqs">
    <div className="mx-auto max-w-3xl px-6">
      <h2 className="font-display text-center text-4xl font-light text-neutral-950 md:text-5xl">Questions, answered</h2>
      <Accordion type="single" collapsible className="mt-10">
        {p.faqs.map(([q, a], i) => (
          <AccordionItem key={q} value={q} className="border-neutral-300" data-testid={`tp-faq-${i}`}>
            <AccordionTrigger className="text-left font-display text-xl font-normal hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-sm font-light leading-relaxed text-neutral-600 md:text-base">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="mt-12 flex flex-col items-center gap-4 text-center">
        <Cta p={p} book={book} className="btn-ink" testid="tp-bottom-book-btn">Book Your Consultation</Cta>
        <p className="text-sm font-light text-neutral-600">Questions first? <a href={`sms:${PHONE_TEL}`} className="underline" data-testid="tp-text-link">Text us</a> or read our <Link to={`/blog/${p.article}`} className="underline" data-testid="tp-article-link">Journal guide</Link>.</p>
        <Link to="/treatments" className="link-under mt-4" data-testid="tp-menu-link">View the Full Menu</Link>
      </div>
    </div>
  </section>
);

export default function TreatmentPage() {
  const { slug } = useParams();
  const p = TREATMENT_PAGES.find((x) => x.slug === slug);
  const book = useBookingLink(p?.service);
  useFaqSchema(p);
  if (!p) return <main className="px-6 py-32 text-center" data-testid="tp-not-found"><p className="font-display text-3xl">Treatment not found.</p><Link to="/treatments" className="link-under mt-6">View all treatments</Link></main>;
  return (
    <main data-testid="treatment-page">
      <Hero p={p} book={book} />
      <Treats p={p} />
      <Steps p={p} />
      {p.pricing && <Pricing p={p} book={book} />}
      <Faqs p={p} book={book} />
    </main>
  );
}
